import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useStore } from '../context/StoreContext';
import { TRANSLATIONS } from '../constants';
import { ClipboardList, ArrowRight, ShoppingBag, Package, ChevronRight } from 'lucide-react';
import { updateMetaTags } from '../utils/seo';
import { SEO_CONFIG, getFullUrl } from '../config/seo.config';
import { Order } from '../types';

const getStatusColor = (status: Order['status']) => {
  switch (status) {
    case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case 'processing': return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'shipped': return 'bg-purple-100 text-purple-800 border-purple-200';
    case 'delivered': return 'bg-green-100 text-green-800 border-green-200';
    case 'cancelled': return 'bg-red-100 text-red-800 border-red-200';
    default: return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

const getStatusIcon = (status: Order['status']) => {
  switch (status) {
    case 'pending': return <span className="w-2 h-2 rounded-full bg-yellow-500" />;
    case 'processing': return <span className="w-2 h-2 rounded-full bg-blue-500" />;
    case 'shipped': return <span className="w-2 h-2 rounded-full bg-purple-500" />;
    case 'delivered': return <span className="w-2 h-2 rounded-full bg-green-500" />;
    case 'cancelled': return <span className="w-2 h-2 rounded-full bg-red-500" />;
    default: return <span className="w-2 h-2 rounded-full bg-gray-500" />;
  }
};

export const MyOrders = () => {
  const { myOrders, language } = useStore();
  const navigate = useNavigate();
  const t = TRANSLATIONS;

  useEffect(() => {
    updateMetaTags({
      title: `${t.myOrders[language]} - ${SEO_CONFIG.storeName}`,
      description: language === 'en' ? 'View your order history' : 'Zobacz historię zamówień',
      canonical: getFullUrl('/orders'),
    });
  }, [language, t]);

  const sortedOrders = [...myOrders].sort((a, b) => {
    return new Date(b.date).getTime() - new Date(a.date).getTime();
  });

  if (sortedOrders.length === 0) {
    return (
      <main className="flex flex-col items-center justify-center py-20 text-center px-4">
        <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-6">
          <ClipboardList className="w-10 h-10 text-gray-400" />
        </div>
        <h2 className="text-2xl font-semibold text-gray-900 mb-2">{t.noOrders[language]}</h2>
        <p className="text-gray-500 mb-6 max-w-sm">
          {language === 'en'
            ? 'You have not placed any orders yet. Start shopping to see your orders here.'
            : 'Jeszcze nie złożyłeś żadnych zamówień. Zacznij zakupy, aby zobaczyć swoje zamówienia tutaj.'}
        </p>
        <button
          onClick={() => navigate('/')}
          className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 px-8 rounded-xl transition-all shadow-lg shadow-emerald-200 flex items-center gap-2"
        >
          <ShoppingBag className="w-5 h-5" />
          {t.continueShopping[language]}
        </button>
      </main>
    );
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'pl' ? 'pl-PL' : 'en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  return (
    <main className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6 md:mb-8">
        <h1 className="text-2xl font-bold text-gray-900">{t.myOrders[language]}</h1>
        <span className="text-sm text-gray-500">
          {sortedOrders.length} {language === 'en' ? (sortedOrders.length === 1 ? 'order' : 'orders') : (sortedOrders.length === 1 ? 'zamówienie' : sortedOrders.length < 5 ? 'zamówienia' : 'zamówień')}
        </span>
      </div>

      <div className="space-y-4">
        {sortedOrders.map((order) => (
          <div
            key={order.id}
            className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
          >
            <div className="p-4 md:p-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-bold text-gray-900">
                      {t.orderNumber[language]}{order.id}
                    </h3>
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusColor(order.status)}`}>
                      {getStatusIcon(order.status)}
                      {t[order.status]?.[language] || order.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500">
                    {t.orderDate[language]}: {formatDate(order.date)}
                  </p>
                  <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                    <span className="flex items-center gap-1">
                      <Package className="w-4 h-4" />
                      {order.items.length} {language === 'en' ? (order.items.length === 1 ? 'item' : 'items') : (order.items.length === 1 ? 'produkt' : order.items.length < 5 ? 'produkty' : 'produktów')}
                    </span>
                    <span className="font-semibold text-gray-900">
                      {order.total.toFixed(2)} zł
                    </span>
                  </div>
                </div>

                <Link
                  to={`/orders/${order.id}`}
                  className="flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-2.5 px-5 rounded-lg transition-colors text-sm whitespace-nowrap"
                >
                  {t.viewOrder[language]}
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        ))}
      </div>
    </main>
  );
};
